/**
 * Configuration du contenu selon le type d'utilisateur
 *
 * Le choix des composants (SideMenu, Drawer, TabBar, TopBar) est fait
 * directement dans NavigationLayout.vue selon layoutContext.
 *
 * Ce fichier définit uniquement QUOI afficher dans ces composants.
 *
 * @module layouts/constants/userContentConfig
 */

import { StudentRouteName } from '@/router/constants/routesName';
import { UserType } from '@/common/types/userTypes';
import { USER_TYPE } from '@/common/constants';

// ============================================
// TYPES
// ============================================

/**
 * Configuration du contenu selon le type d'utilisateur
 */
export interface UserContentConfig {
  /** Afficher les catégories de navigation */
  showCategories: boolean;

  /** Afficher les routes utilisateur (accès rapides) */
  showUserRoutes: boolean;

  /** Afficher la barre de recherche rapide */
  showQuickSearch: boolean;

  /** Afficher les outils (notifications, etc.) */
  showTools: boolean;

  /** Afficher le bouton Chat */
  showChat: boolean;

  /** Noms des routes exclues du menu */
  excludedRouteNames: string[];
}

/**
 * Type pour les routes utilisateur normalisées
 * Utilisé pour uniformiser la structure des routes dans les composants
 */
export interface NormalizedUserRoute {
  /** Nom unique de la route */
  name: string;

  /** Chemin de la route */
  path: string;

  /** Métadonnées de la route */
  meta?: {
    /** Clé de traduction du titre */
    title?: string;

    /** Nom de l'icône */
    icon?: string;

    /** Rôles autorisés à accéder à cette route */
    roles?: string[];

    /** Ordre d'affichage dans les menus */
    order?: number;
  };
}

// ============================================
// CONFIGURATION PAR TYPE D'UTILISATEUR
// ============================================

/**
 * Configuration du contenu des menus par type d'utilisateur
 *
 * Définit QUOI afficher dans la navigation
 * Le COMMENT (quel composant) est déterminé par layoutContext dans NavigationLayout.vue
 */
/** Configuration commune pour le staff (Collaborateur & Administrator) */
const STAFF_CONTENT: UserContentConfig = {
  showCategories: false,
  showUserRoutes: true,
  showQuickSearch: false,
  showTools: false,
  showChat: false,
  excludedRouteNames: [],
};

export const USER_CONTENT: Record<UserType, UserContentConfig> = {
  /**
   * Étudiants : accès complet avec catégories et recherche
   */
  [USER_TYPE.STUDENT]: {
    showCategories: true,
    showUserRoutes: true,
    showQuickSearch: true,
    showTools: true,
    showChat: true,
    excludedRouteNames: [
      StudentRouteName.Chat,
      StudentRouteName.Shop,
      StudentRouteName.AroundTheWorld,
      StudentRouteName.StudentHome,
      StudentRouteName.PartnershipUniversities,
    ],
  },

  /**
   * Collaborateurs : navigation simplifiée
   */
  [USER_TYPE.COLLABORATEUR]: { ...STAFF_CONTENT },

  /**
   * Administrateurs : même base que collaborateur, à spécialiser si besoin
   */
  [USER_TYPE.ADMINISTRATOR]: { ...STAFF_CONTENT },
};

// ============================================
// CONFIGURATION PAR DÉFAUT
// ============================================

/**
 * Configuration par défaut (si type non trouvé)
 * Fallback sur Student car l'authentification est obligatoire
 */
export const DEFAULT_USER_CONTENT: UserContentConfig = USER_CONTENT[USER_TYPE.STUDENT];
